/**
 * Optional boat polar: boat speed through the water as a function of the true
 * wind angle (TWA) and true wind speed (TWS). When the user sets one, the
 * auto-router uses it for genuine sail routing: a boat can't sail straight into
 * the wind, so directly-upwind legs come back near zero speed and the optimal
 * grid path zigzags (tacks) up the windward leg by itself. With no polar set,
 * the router just motors at cruise speed and this is unused.
 *
 * The model is a compact training one: a speed curve at a reference wind speed,
 * interpolated by TWA and scaled by how the real wind compares to the reference.
 * Not a measured VPP polar; enough to make the routing behave like a sailing boat.
 */

export interface PolarPoint {
  /** True wind angle, 0 (head to wind) … 180 (dead run), degrees. */
  twaDeg: number;
  /** Boat speed through the water at the reference TWS, knots. */
  speedKn: number;
}

export interface BoatPolar {
  /** The true wind speed (kn) the curve is given at. */
  twsRef: number;
  /** Speed curve, sorted by twaDeg ascending, 0…180. */
  points: PolarPoint[];
}

/** A typical mid-size cruising yacht, for a sensible starting point. */
export const DEFAULT_POLAR: BoatPolar = {
  twsRef: 12,
  points: [
    { twaDeg: 0, speedKn: 0 },
    { twaDeg: 30, speedKn: 0 }, // inside the no-go zone: can't make ground to windward
    { twaDeg: 40, speedKn: 4.6 }, // close-hauled
    { twaDeg: 52, speedKn: 5.7 },
    { twaDeg: 70, speedKn: 6.3 },
    { twaDeg: 90, speedKn: 6.6 }, // beam reach, quickest
    { twaDeg: 110, speedKn: 6.6 },
    { twaDeg: 135, speedKn: 6.1 },
    { twaDeg: 150, speedKn: 5.3 },
    { twaDeg: 180, speedKn: 4.4 }, // dead run
  ],
};

/** The true wind angle (0…180) between a heading and the wind's FROM direction. */
export function trueWindAngle(headingDeg: number, windFromDeg: number): number {
  let d = (((headingDeg - windFromDeg) % 360) + 360) % 360;
  if (d > 180) d = 360 - d;
  return d;
}

/**
 * Boat speed through the water from the polar, at a true wind angle and true
 * wind speed. Interpolates the curve by TWA and scales roughly by TWS.
 */
export function boatSpeedFromPolar(polar: BoatPolar, twaDeg: number, twsKn: number): number {
  let twa = Math.abs((((twaDeg % 360) + 360) % 360));
  if (twa > 180) twa = 360 - twa;
  const pts = polar.points;
  if (!pts.length) return 0;

  let base: number;
  if (twa <= pts[0].twaDeg) base = pts[0].speedKn;
  else if (twa >= pts[pts.length - 1].twaDeg) base = pts[pts.length - 1].speedKn;
  else {
    let i = 0;
    while (i < pts.length - 1 && pts[i + 1].twaDeg < twa) i++;
    const a = pts[i];
    const b = pts[i + 1];
    const t = (twa - a.twaDeg) / (b.twaDeg - a.twaDeg);
    base = a.speedKn + (b.speedKn - a.speedKn) * t;
  }
  const scale = Math.max(0.25, Math.min(1.6, polar.twsRef > 0 ? twsKn / polar.twsRef : 1));
  return Math.max(0, base * scale);
}
